import CardTitle from 'app/components/CardTitle';
import ErrorBoundary from 'app/components/ErrorBoundary';
import ActionsCell from 'app/components/Tables/Cells/ActionsCell';
import AvatarCell from 'app/components/Tables/Cells/AvatarCell';
import PrimaryMainTable from 'app/components/Tables/PrimaryMainTable';
import { useModal } from 'app/context/Modal';
import { dateTimeFormatter } from 'app/utils/dateTimeFormatter';
import { _t } from 'app/utils/i18n';
import { nFormatter } from 'app/utils/numbersFormater';
import { path } from 'app/utils/path';
import { getSearchState, setSearchState } from 'app/utils/search-state';
import { regrabFollowingTwitter, storeInfluencerName } from 'influencers/Requests';
import {
  entitiesApiRoutes,
  influencerApiRoutes,
} from 'influencers/utils/apiRoutes';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { headerCell } from 'trends/components/TrendsCustmComps/TrendsCustmComps';

import styles from './TwitterFollowingTab.module.scss';

interface Props {
  isInfluencerModule?: boolean;
  compId: string;
  id?: number;
  isReport?: boolean;
  twitter_profile_id?: number;
  influencer_id: number;
  customFilters?: Record<string, unknown>;
}

const TwitterFollowingTab: FunctionComponent<Props> = ({
  isInfluencerModule,
  compId,
  id,
  isReport,
  twitter_profile_id,
  influencer_id,
  customFilters,
}) => {
  const modal = useModal();
  const history = useHistory();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [influencerName, setInfluencerName] = useState('');
  const [hideIfNoData, setHideIfNoData] = useState(false);
  const queryClient = useQueryClient();
  const influencerDetails: any = queryClient.getQueryData('influencerDetails');
  const { mutateAsync: addInfluncerNameMutateAsync } = useMutation(
    storeInfluencerName,
    {
      onSuccess: (rec: any) => {
        modal.hide();
        history.push(
          `${path.influencers.UN_ANALYZED_INFLUENCER_DETAILS_URI}/${rec?.id}`
        );
        toast.success(<span>{_t('influencers:added_influencer')}</span>);
      },
      onError: (err: any) => {
        modal.hide();
        toast.error(<span>{err?.response?.data?.message}</span>);
      },
    }
  );
  const addInfluncerNameAsUnanalyzed = async (name: string) => {
    const result = await addInfluncerNameMutateAsync({
      username: name,
    });
    return result;
  };

  const openModalToAddinfluencer = (name: string) => {
    setInfluencerName(name);
    modal.show({
      message: _t('hashtags:add_influencer_as_unanalyzes'),
      onOk: () => {
        addInfluncerNameAsUnanalyzed(name);
      },
      openLink: `https://twitter.com/${name}`,
      linkText: _t('hashtags:open_in_twitter'),
    });
  };
  useEffect(() => {
    const searchState = getSearchState();
    if (searchState) {
      setSearchState({
        ...searchState,
        filters: { ...searchState?.filters, page: 1 },
      });
    }
  }, []);
  const measuredRef = useCallback(
    (node: any) => {
      if (node !== null && isReport) {
        if (node?.currentDataSource?.total_record === 0) {
          setHideIfNoData(true);
        }
      }
    },
    [isReport]
  );

  const { mutateAsync: regrabFollowing } = useMutation(
    regrabFollowingTwitter,
    {
      onSuccess: (rec: any) => {
        modal.show({
          hideCancel: true,
          message: `${_t('basic:working_on_grabbing')} ${_t('influencers:inf_following')} ${_t(
            'basic:you_will_be_notified'
          )}`,
          onOk: () => {
            modal.hide();
          },
          onOkText: _t('basic:ok'),
        });
      },
      onError: (err: any) => {
        modal.hide();
        toast.error(<span>{err?.response?.data?.message}</span>);
      },
    },
  );

  const FollowingRegrabHandler = async()=>{
    await regrabFollowing({
      twitter_profile_id,
      influencer_id: influencerDetails?.influencer_id,
      isInfluencerModule
    })
  }

  const twitterFollowingTab = [
    {
      name: 'username',
      header: headerCell('influencers'),
      defaultFlex: 1,
      render: (value: any) => {
        const influencerId = value?.data?.influencer?.influencer_id;
        const is_entity = value?.data?.influencer?.is_entity;
        const is_influencer = value?.data?.influencer?.is_influencer;
        const username = value?.data?.username;
        const name = value?.data?.influencer?.name?.original || value?.data?.username;
        return (
          <>
            <div className="actionsCell">
              {influencerId !== 0 ? (
                <AvatarCell
                  img={value?.data?.image_url}
                  imageIsClick
                  text={name}
                  goTo={`${
                    is_entity
                      ? path.entities.ANALYZED_ENTITIES_DETAILS_URI
                      : is_influencer
                      ? path.influencers.ANALYZED_INFLUENCER_DETAILS_URI
                      : path.influencers.UN_ANALYZED_INFLUENCER_DETAILS_URI
                  }/${influencerId}`}
                />
              ) : (
                <div
                  onClick={() => {
                    openModalToAddinfluencer(username);
                  }}
                  className="cursor-pointer"
                >
                  <AvatarCell
                    img={value?.data?.image_url}
                    imageIsClick
                    text={name}
                  />
                </div>
              )}
              <div className="actions">
                <ActionsCell hideChart hideBookmark rowData={value.data} />
              </div>
            </div>
          </>
        );
      },
    },
    {
      name: 'followers_count',
      header: headerCell('Followers'),
      defaultFlex: 1,
      render: ({ data }: any) => (
        <p>
          {!data?.last_tweet_time && data?.followers_count === 0
            ? '_'
            : nFormatter(data?.followers_count)}
        </p>
      ),
    },
    {
      name: 'following_count',
      header: headerCell('Following'),
      defaultFlex: 1,
      render: ({ data }: any) => (
        <p>
          {!data?.last_tweet_time && data?.following_count === 0
            ? '_'
            : nFormatter(data?.following_count)}
        </p>
      ),
    },
    {
      name: 'tweets_count',
      header: headerCell('Tweets'),
      defaultFlex: 1,
      render: ({ data }: any) => (
        <p>
          {!data?.last_tweet_time && data?.tweets_count === 0
            ? '_'
            : nFormatter(data?.tweets_count)}
        </p>
      ),
    },
    {
      name: 'last_tweet_time',
      header: headerCell('last_tweet'),
      defaultFlex: 1,
      render: ({ data }: any): any => (
        <p>
          {data?.last_tweet_time
            ? dateTimeFormatter.date(data?.last_tweet_time)
            : '_'}
        </p>
      ),
    },
  ];
  const ref = useRef<any>();
  useEffect(() => {
    ref.current?.loadData({ page: 1 });
  }, [ref.current]);
  if (hideIfNoData) return <></>;
  return (
    <div className={styles.TwitterFollowingTab} id={compId}>
      {isReport && (
        <CardTitle
          title={_t('basic:following')}
          compId={compId}
          hideIcon={isReport}
        />
      )}
      <ErrorBoundary>
        <PrimaryMainTable
          moduleName={
            isInfluencerModule
              ? influencerApiRoutes().twitterFollowingTab
              : entitiesApiRoutes().twitterFollowingTab
          }
          activeTab="twitter_following"
          initialFilters={{
            influencer_id: +influencer_id || id,
            ...customFilters,
          }}
          title={_t('influencers:Following')}
          defaultTab="twitter_following"
          showRegrabIcon={true}
          regrabIconFunction={FollowingRegrabHandler}
          customRequiredFilters={{ twitter_profile_id,influencer_id: +influencer_id  }}
          columns={twitterFollowingTab}
          hideCheckbox
          stopSearchFilters={true}
          hidePagination={isReport}
          initFilter={{ page: 1 }}
          ref={isReport ? measuredRef : ref}
          compId={''}
        />
      </ErrorBoundary>
    </div>
  );
};

export default TwitterFollowingTab;
